import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { DollarSign, Clock, SquareIcon } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";

interface ServiceData {
  id: string;
  name: string;
  description: string;
  type: "cleaning" | "floorcare";
  pricingMethod: "hourly" | "squareFootage";
  frequency: string;
  serviceDays: string[];
  hourlyRate: number;
  estimatedHours: number;
  squareFootage: number;
  ratePerSqFt?: number;
  productionRate: number;
  overheadPercentage?: number;
  profitMargin?: number;
  totalPrice: number;
}

interface EditCleaningServiceSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (service: any) => void;
  service: ServiceData;
  bidId: Id<"bids">;
}

export function EditCleaningServiceSheet({
  isOpen,
  onClose,
  onUpdate = () => {},
  service,
  bidId,
}: EditCleaningServiceSheetProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pricingMethod, setPricingMethod] = useState<"hourly" | "squareFootage">(
    service.pricingMethod || "hourly"
  );
  const [isCalculationApplied, setIsCalculationApplied] = useState(
    service.squareFootage > 0 && service.productionRate > 0
  );
  // Ensure numeric values are properly initialized
  const getNumericValue = (value: any) => {
    if (value === undefined || value === null) return 0;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const [serviceData, setServiceData] = useState({
    id: service.id,
    name: service.name || "",
    description: service.description || "",
    frequency: service.frequency || "5_per_week",
    serviceDays: service.serviceDays || ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    hourlyRate: getNumericValue(service.hourlyRate),
    estimatedHours: getNumericValue(service.estimatedHours),
    squareFootage: getNumericValue(service.squareFootage),
    ratePerSqFt: getNumericValue(service.ratePerSqFt),
    productionRate: getNumericValue(service.productionRate),
    overheadPercentage: getNumericValue(service.overheadPercentage),
    profitMargin: getNumericValue(service.profitMargin),
    type: service.type as "cleaning" | "floorcare",
  });

  // Convex mutation for updating a service
  const updateServiceMutation = useMutation(api.bidPricing.update);

  // Log the initial service data for debugging
  console.log("Initial service data:", {
    id: service.id,
    name: service.name,
    ratePerSqFt: service.ratePerSqFt,
    ratePerSqFtType: typeof service.ratePerSqFt,
    ratePerSqFtParsed: getNumericValue(service.ratePerSqFt),
    pricingMethod: service.pricingMethod,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    // For numeric fields, handle empty strings and ensure decimal values are preserved
    let newValue: string | number;
    if (["name", "description"].includes(id)) {
      newValue = value;
    } else {
      // For numeric fields, use parseFloat but handle empty strings
      newValue = value === "" ? 0 : parseFloat(value);
      // Ensure NaN values are converted to 0
      if (isNaN(newValue)) newValue = 0;
    }

    const updatedData = {
      ...serviceData,
      [id]: newValue,
    };

    // Calculate estimated hours if both square footage and production rate are > 0
    if (
      (id === "squareFootage" || id === "productionRate") &&
      updatedData.squareFootage > 0 &&
      updatedData.productionRate > 0
    ) {
      // Calculate and update the estimated hours
      const calculatedHours = updatedData.squareFootage / updatedData.productionRate;
      updatedData.estimatedHours = parseFloat(calculatedHours.toFixed(2));

      // Reset the applied state when square footage or production rate changes
      setIsCalculationApplied(false);

      // Log the calculation for debugging
      console.log(
        `Calculated hours: ${calculatedHours.toFixed(2)} from ${updatedData.squareFootage} sq ft at ${updatedData.productionRate} sq ft/hr`
      );
    }

    // Log changes to ratePerSqFt for debugging
    if (id === "ratePerSqFt") {
      console.log("Rate per sq ft changed:", {
        oldValue: serviceData.ratePerSqFt,
        newValue: updatedData.ratePerSqFt,
        rawInputValue: value,
      });
    }

    setServiceData(updatedData);
  };

  const handleSelectChange = (id: string, value: string) => {
    if (id === "frequency" && pricingMethod === "squareFootage") {
      toast({
        title: "Frequency Locked",
        description:
          "Square footage pricing method is only available for 5 days per week frequency.",
        variant: "destructive",
      });
      return;
    }

    setServiceData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handlePricingMethodChange = (method: "hourly" | "squareFootage") => {
    setPricingMethod(method);
    if (method === "squareFootage") {
      setServiceData((prev) => ({
        ...prev,
        frequency: "5_per_week",
      }));
    }
  };

  const calculateHourlyPrice = () => {
    const { hourlyRate, estimatedHours, frequency, overheadPercentage, profitMargin } = serviceData;

    // Calculate base cost for a single service
    const singleServiceCost = hourlyRate * estimatedHours;

    // Calculate services per month based on frequency
    let servicesPerMonth = 0;

    if (frequency.includes("per_week")) {
      const timesPerWeek = parseInt(frequency.split("_")[0]);
      servicesPerMonth = timesPerWeek * 4.33; // Average weeks per month
    } else if (frequency.includes("per_month")) {
      const timesPerMonth = parseInt(frequency.split("_")[0]);
      servicesPerMonth = timesPerMonth;
    }

    // Calculate monthly base price
    let monthlyPrice = singleServiceCost * servicesPerMonth;

    // Apply overhead if provided
    if (overheadPercentage > 0) {
      const overheadAmount = monthlyPrice * (overheadPercentage / 100);
      monthlyPrice += overheadAmount;
    }

    // Apply profit margin if provided
    if (profitMargin > 0) {
      const profitAmount = monthlyPrice * (profitMargin / 100);
      monthlyPrice += profitAmount;
    }

    return monthlyPrice;
  };

  const calculateSquareFootagePrice = () => {
    const { squareFootage, ratePerSqFt, overheadPercentage, profitMargin } = serviceData;

    // Calculate monthly base price directly
    const monthlyBasePrice = squareFootage * ratePerSqFt;
    let monthlyPrice = monthlyBasePrice;

    // Apply overhead if provided
    if (overheadPercentage > 0) {
      const overheadAmount = monthlyBasePrice * (overheadPercentage / 100);
      monthlyPrice += overheadAmount;
    }

    // Apply profit margin if provided
    if (profitMargin > 0) {
      const profitAmount = monthlyBasePrice * (profitMargin / 100);
      monthlyPrice += profitAmount;
    }

    return monthlyPrice;
  };

  const totalPrice =
    pricingMethod === "hourly" ? calculateHourlyPrice() : calculateSquareFootagePrice();

  const validateForm = () => {
    if (!serviceData.name) {
      toast({
        title: "Validation Error",
        description: "Please select a service",
        variant: "destructive",
      });
      return false;
    }

    if (serviceData.name === "custom" && !serviceData.description) {
      toast({
        title: "Validation Error",
        description: "Please enter a description for the custom service",
        variant: "destructive",
      });
      return false;
    }

    if (pricingMethod === "hourly" && !serviceData.estimatedHours) {
      toast({
        title: "Validation Error",
        description: "Please enter estimated hours",
        variant: "destructive",
      });
      return false;
    }

    if (pricingMethod === "squareFootage" && !serviceData.squareFootage) {
      toast({
        title: "Validation Error",
        description: "Please enter square footage",
        variant: "destructive",
      });
      return false;
    }

    if (!serviceData.serviceDays.length) {
      toast({
        title: "Validation Error",
        description: "Please select at least one service day",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      // Explicitly create the service payload with all required fields
      const servicePayload = {
        name: serviceData.name,
        description: serviceData.description,
        frequency: serviceData.frequency,
        serviceDays: serviceData.serviceDays,
        pricingMethod,
        hourlyRate: serviceData.hourlyRate,
        estimatedHours: serviceData.estimatedHours,
        // Always include squareFootage and productionRate regardless of pricing method
        squareFootage: serviceData.squareFootage,
        ratePerSqFt: serviceData.ratePerSqFt,
        productionRate: serviceData.productionRate,
        overheadPercentage: serviceData.overheadPercentage,
        profitMargin: serviceData.profitMargin,
        totalPrice: totalPrice,
        type: serviceData.type as "cleaning" | "floorcare", // Use const assertion to ensure it's treated as a literal
      };

      // Log the service payload before updating
      console.log("Service payload being sent to Convex:", {
        ...servicePayload,
        ratePerSqFt: servicePayload.ratePerSqFt,
        ratePerSqFtType: typeof servicePayload.ratePerSqFt,
      });

      // Update the service in Convex
      await updateServiceMutation({
        id: service.id as Id<"bidPricing">,
        serviceData: servicePayload,
      });

      // Call the onUpdate callback with the updated service data
      await onUpdate(servicePayload);

      toast({
        title: "Success",
        description: "Service updated successfully",
      });
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description:
          "Failed to update service: " + (error instanceof Error ? error.message : "Unknown error"),
        variant: "destructive",
      });
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Edit Cleaning Service</SheetTitle>
          <SheetDescription>Update the cleaning service details.</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            {/* Service name selection */}
            <div className="space-y-2">
              <Label htmlFor="name">Service</Label>
              <Select
                value={serviceData.name}
                onValueChange={(value) => handleSelectChange("name", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="General Cleaning">General Cleaning</SelectItem>
                  <SelectItem value="Post Construction Cleaning">
                    Post Construction Cleaning
                  </SelectItem>
                  <SelectItem value="Disinfectant Cleaning">Disinfectant Cleaning</SelectItem>
                  <SelectItem value="Restroom Cleaning">Restroom Cleaning</SelectItem>
                  <SelectItem value="Exterior Cleaning">Exterior Cleaning</SelectItem>
                  <SelectItem value="Pressure Washing">Pressure Washing</SelectItem>
                  <SelectItem value="Tile & Grout Cleaning">Tile & Grout Cleaning</SelectItem>
                  <SelectItem value="Window & Glass Cleaning">Window & Glass Cleaning</SelectItem>
                  <SelectItem value="Trash Chute & Dumpster Area Cleaning">
                    Trash Chute & Dumpster Area Cleaning
                  </SelectItem>
                  <SelectItem value="Commercial Kitchen Cleaning">
                    Commercial Kitchen Cleaning
                  </SelectItem>
                  <SelectItem value="Graffiti Removal">Graffiti Removal</SelectItem>
                  <SelectItem value="Odor Removal">Odor Removal</SelectItem>
                  <SelectItem value="Upholstery & Fabric Cleaning">
                    Upholstery & Fabric Cleaning
                  </SelectItem>
                  <SelectItem value="Warehouse & Storage Area Cleaning">
                    Warehouse & Storage Area Cleaning
                  </SelectItem>
                  <SelectItem value="High-Touch Surface Sanitization">
                    High-Touch Surface Sanitization
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Service Description</Label>
              <Input
                id="description"
                placeholder="Brief description of the service"
                value={serviceData.description}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-4">
              <Label>Pricing Method</Label>
              <RadioGroup
                value={pricingMethod}
                onValueChange={handlePricingMethodChange}
                className="flex space-x-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="hourly"
                    id="hourly"
                    className="border-blue-600 text-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
                  />
                  <Label
                    htmlFor="hourly"
                    className={`${pricingMethod === "hourly" ? "text-gray-900" : "text-gray-400"}`}
                  >
                    Hourly Rate
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="squareFootage"
                    id="squareFootage"
                    className="border-blue-600 text-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white"
                  />
                  <Label
                    htmlFor="squareFootage"
                    className={`${pricingMethod === "squareFootage" ? "text-gray-900" : "text-gray-400"}`}
                  >
                    Square Footage
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <Tabs value={pricingMethod} className="mt-6">
              <TabsContent value="hourly" className="space-y-4">
                <div className="mb-3 text-sm text-gray-600">
                  Hourly method works best for locations less than 10,000 sq ft.
                </div>

                {/* Optional Square Footage and Production Rate fields */}
                <div className="mb-4 rounded-md bg-gray-50 p-3">
                  <h4 className="mb-2 text-sm font-medium">
                    Optional: Calculate Hours from Square Footage
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="squareFootage">Areas (sq ft)</Label>
                        <div className="group relative">
                          <span className="cursor-help text-gray-500 hover:text-gray-700">?</span>
                          <div className="invisible absolute right-0 z-10 mt-1 w-44 rounded border bg-white p-2 text-xs shadow-lg transition-opacity group-hover:visible">
                            Areas that require cleaning
                          </div>
                        </div>
                      </div>
                      <Input
                        id="squareFootage"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0"
                        value={serviceData.squareFootage}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="productionRate">Production Rate (sq ft/hr)</Label>
                        <div className="group relative">
                          <span className="cursor-help text-gray-500 hover:text-gray-700">?</span>
                          <div className="invisible absolute right-0 z-10 mt-1 w-64 rounded border bg-white p-2 text-xs shadow-lg transition-opacity group-hover:visible">
                            <div className="mb-1 font-semibold">Average Production Rates</div>
                            General cleaning: 2000-3000 sq ft/hr
                            <br />
                            Detailed cleaning: 1000-2000 sq ft/hr
                            <br />
                            Deep cleaning: 500-1000 sq ft/hr
                          </div>
                        </div>
                      </div>
                      <Input
                        id="productionRate"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0"
                        value={serviceData.productionRate}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  {serviceData.squareFootage > 0 && serviceData.productionRate > 0 && (
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-sm text-blue-600">
                        Estimated hours:{" "}
                        {(serviceData.squareFootage / serviceData.productionRate).toFixed(2)} hours
                      </p>
                      <Button
                        type="button"
                        variant={isCalculationApplied ? "default" : "outline"}
                        size="sm"
                        className={
                          isCalculationApplied ? "bg-green-600 text-white hover:bg-green-700" : ""
                        }
                        onClick={() => {
                          const calculatedHours =
                            serviceData.squareFootage / serviceData.productionRate;
                          setServiceData((prev) => ({
                            ...prev,
                            estimatedHours: parseFloat(calculatedHours.toFixed(2)),
                            // Ensure squareFootage and productionRate are explicitly saved in state
                            squareFootage: prev.squareFootage,
                            productionRate: prev.productionRate,
                          }));

                          // Set the applied state to true
                          setIsCalculationApplied(true);

                          // Log the updated state for debugging
                          console.log("Applied square footage calculation:", {
                            squareFootage: serviceData.squareFootage,
                            productionRate: serviceData.productionRate,
                            calculatedHours: parseFloat(calculatedHours.toFixed(2)),
                          });
                        }}
                      >
                        {isCalculationApplied ? (
                          <span className="flex items-center">
                            <svg
                              className="mr-1 h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            Applied
                          </span>
                        ) : (
                          "Apply"
                        )}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Hourly Rate and Estimated Hours fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="hourlyRate">Hourly Rate ($)</Label>
                    <Input
                      id="hourlyRate"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={serviceData.hourlyRate}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="estimatedHours">Estimated Hours</Label>
                    <div className="relative">
                      <Input
                        id="estimatedHours"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0"
                        value={serviceData.estimatedHours}
                        onChange={(e) => {
                          handleInputChange(e);
                          // Reset the applied state when manually changing the estimated hours
                          setIsCalculationApplied(false);
                        }}
                        className={
                          isCalculationApplied ? "border-green-500 focus:ring-green-500" : ""
                        }
                      />
                      {isCalculationApplied && (
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 transform text-green-600">
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="squareFootage" className="space-y-4">
                <div className="mb-3 text-sm text-gray-600">
                  Square footage method works best for locations greater than 10,000 sq ft.
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="squareFootage">Square Footage</Label>
                    <Input
                      id="squareFootage"
                      type="number"
                      min="0"
                      placeholder="0"
                      value={serviceData.squareFootage}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ratePerSqFt">Rate per Sq Ft ($)</Label>
                    <Input
                      id="ratePerSqFt"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={serviceData.ratePerSqFt}
                      onChange={(e) => {
                        // Special handling for rate per sq ft to ensure decimal values are preserved
                        const rawValue = e.target.value;
                        console.log("Rate per sq ft input value:", {
                          rawValue,
                          parsedValue: rawValue === "" ? 0 : parseFloat(rawValue),
                        });
                        handleInputChange(e);
                      }}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <div className="space-y-2">
              <Label htmlFor="frequency">Cleaning Frequency</Label>
              <Select
                value={serviceData.frequency}
                onValueChange={(value) => handleSelectChange("frequency", value)}
                disabled={pricingMethod === "squareFootage"}
              >
                <SelectTrigger className={pricingMethod === "squareFootage" ? "bg-gray-100" : ""}>
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7_per_week">7 Days per Week</SelectItem>
                  <SelectItem value="6_per_week">6 Days per Week</SelectItem>
                  <SelectItem value="5_per_week">5 Days per Week</SelectItem>
                  <SelectItem value="4_per_week">4 Days per Week</SelectItem>
                  <SelectItem value="3_per_week">3 Days per Week</SelectItem>
                  <SelectItem value="2_per_week">2 Days per Week</SelectItem>
                  <SelectItem value="1_per_week">1 Day per Week</SelectItem>
                  <SelectItem value="2_per_month">Bi-Monthly</SelectItem>
                  <SelectItem value="1_per_month">Monthly</SelectItem>
                </SelectContent>
              </Select>
              {pricingMethod === "squareFootage" && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Square footage pricing is fixed at 5 days per week
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="serviceDays">Preferred Service Days</Label>
              <div className="flex flex-wrap gap-2">
                {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(
                  (day) => (
                    <button
                      key={day}
                      type="button"
                      className={`rounded-full border px-3 py-1.5 text-sm transition-all ${
                        serviceData.serviceDays?.includes(day)
                          ? "border-blue-500 bg-blue-500 text-white"
                          : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
                      }`}
                      onClick={() => {
                        const currentDays = serviceData.serviceDays || [];
                        const newDays = currentDays.includes(day)
                          ? currentDays.filter((d) => d !== day)
                          : [...currentDays, day];
                        setServiceData({
                          ...serviceData,
                          serviceDays: newDays,
                        });
                      }}
                    >
                      {day.substring(0, 3)}
                    </button>
                  )
                )}
              </div>
            </div>

            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="overheadPercentage">Overhead (%) (optional)</Label>
                <Input
                  id="overheadPercentage"
                  type="number"
                  step="0.01"
                  min="0"
                  value={serviceData.overheadPercentage}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setServiceData((prev) => ({
                      ...prev,
                      overheadPercentage: value,
                    }));
                  }}
                  placeholder="0"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="profitMargin">Profit Margin (%) (optional)</Label>
                <Input
                  id="profitMargin"
                  type="number"
                  step="0.01"
                  min="0"
                  value={serviceData.profitMargin}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    setServiceData((prev) => ({
                      ...prev,
                      profitMargin: value,
                    }));
                  }}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="mt-6 rounded-md bg-blue-50 p-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Monthly Price:</span>
                <span className="flex items-center text-xl font-bold text-blue-700">
                  <DollarSign className="h-5 w-5" />
                  {totalPrice.toFixed(2)}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                {pricingMethod === "hourly"
                  ? `Based on ${serviceData.estimatedHours} hours at $${serviceData.hourlyRate}/hour (${serviceData.frequency.replace("_", " ").replace("per", "per")})`
                  : `Based on ${serviceData.squareFootage} sq ft at $${serviceData.ratePerSqFt.toFixed(2)}/sq ft (5 days per week)`}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {serviceData.overheadPercentage > 0 || serviceData.profitMargin > 0 ? (
                  <>
                    Includes
                    {serviceData.overheadPercentage > 0 &&
                      ` ${serviceData.overheadPercentage}% overhead`}
                    {serviceData.overheadPercentage > 0 && serviceData.profitMargin > 0 && " and"}
                    {serviceData.profitMargin > 0 && ` ${serviceData.profitMargin}% profit margin`}
                  </>
                ) : (
                  "No overhead or profit margin included"
                )}
              </p>
            </div>

            <Button className="mt-6 w-full" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Updating Service..." : "Update Service"}
            </Button>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
